/**
 * Analytics contract (Palier 1E) — period handling, query schemas and the
 * response DTOs shared by the API (ClickHouse reads) and the dashboard.
 */

import { z } from 'zod';
import type { BotCategory, SourceType } from './ingest';

export const PERIODS = ['today', 'yesterday', '7d', '14d', '30d', '1y', 'custom'] as const;
export type Period = (typeof PERIODS)[number];

/**
 * Google Ads `DURING` accepts only a fixed enum (TODAY, YESTERDAY, LAST_7/14/
 * 30_DAYS, LAST_MONTH…). Only these short windows map to one; longer or custom
 * periods have no enum and honestly fall back to the last 30 days.
 */
export const ADS_SUPPORTED_PERIODS = ['today', 'yesterday', '7d', '14d', '30d'] as const;
/** The period Google Ads will actually report on (long/custom → `30d`). */
export function adsEffectivePeriod(period: Period): Period {
  return (ADS_SUPPORTED_PERIODS as readonly Period[]).includes(period) ? period : '30d';
}

export interface DateRange {
  from: Date;
  to: Date;
}

const DAY_MS = 86_400_000;

function startOfUtcDay(d: Date): Date {
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
}

/** Resolve a period (relative to `now`) into an absolute UTC date range. */
export function resolvePeriod(
  period: Period,
  now: Date,
  customFrom?: string,
  customTo?: string,
): DateRange {
  switch (period) {
    case 'today':
      return { from: startOfUtcDay(now), to: now };
    case 'yesterday':
      return { from: startOfUtcDay(new Date(now.getTime() - DAY_MS)), to: startOfUtcDay(now) };
    case '7d':
      return { from: new Date(now.getTime() - 7 * DAY_MS), to: now };
    case '14d':
      return { from: new Date(now.getTime() - 14 * DAY_MS), to: now };
    case '30d':
      return { from: new Date(now.getTime() - 30 * DAY_MS), to: now };
    case '1y':
      return { from: new Date(now.getTime() - 365 * DAY_MS), to: now };
    case 'custom': {
      const from = customFrom ? new Date(customFrom) : new Date(now.getTime() - 7 * DAY_MS);
      const to = customTo ? new Date(customTo) : now;
      return { from, to };
    }
  }
}

/** Bucket granularity for time-series: hourly for <= 2 days, else daily. */
export function rangeGranularity(range: DateRange): 'hour' | 'day' {
  return range.to.getTime() - range.from.getTime() <= 2 * DAY_MS ? 'hour' : 'day';
}

// --- Query schemas ---------------------------------------------------------

const isoDate = z.string().datetime({ offset: true }).or(z.string().date());

export const analyticsRangeSchema = z.object({
  period: z.enum(PERIODS).default('7d'),
  from: isoDate.optional(),
  to: isoDate.optional(),
});
export type AnalyticsRangeQuery = z.infer<typeof analyticsRangeSchema>;

export const visitFilterSchema = z.enum([
  'all',
  'humans',
  'bots',
  'suspect',
  'proxy',
  'recidivist',
  // Visits & IP page (V3): traffic source + actually-blocked IPs.
  'ads',
  'organic',
  'blocked',
]);
export type VisitFilter = z.infer<typeof visitFilterSchema>;

export const visitsQuerySchema = analyticsRangeSchema.extend({
  filter: visitFilterSchema.default('all'),
  source: z.string().max(20).optional(),
  country: z.string().max(2).optional(),
  /** Exact IP search — IPv4 or IPv6 (any spelling; canonicalised server-side). */
  ip: z.string().max(64).optional(),
  cursor: z.string().max(256).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
});
export type VisitsQuery = z.infer<typeof visitsQuerySchema>;

export const verdictInputSchema = z
  .object({
    visitId: z.string().uuid().optional(),
    ip: z.string().max(64).optional(),
    verdict: z.enum(['false_positive', 'real_client', 'confirmed_fraud', 'internal_test']),
    note: z.string().max(500).optional(),
  })
  .refine((v) => v.visitId || v.ip, { message: 'visitId or ip required' });
export type VerdictInput = z.infer<typeof verdictInputSchema>;

export const blocklistInputSchema = z.object({
  ip: z.string().min(3).max(64),
  // Default action is "challenge" — the safe-blocking doctrine (§D).
  action: z.enum(['challenge', 'block']).default('challenge'),
  scope: z.enum(['site', 'all']).default('site'),
  reason: z.string().max(500).optional(),
  /** Time-to-live in days; 0 / omitted = no expiry. */
  ttlDays: z.coerce.number().int().min(0).max(365).optional(),
});
export type BlocklistInput = z.infer<typeof blocklistInputSchema>;

// --- Response DTOs ---------------------------------------------------------

/**
 * Plain-language "suspect" cut-off on the 0-100 fraud score. A visit at or above
 * this is surfaced to the client as a suspect click (matches the `suspect` band
 * start in the design risk scale). Unenriched visits keep score 0 (cost rule §7)
 * and therefore read as healthy traffic.
 */
export const SUSPECT_SCORE_THRESHOLD = 50;

export interface OverviewKpis {
  visits: number;
  humans: number;
  bots: number;
  /** Visits whose fraud score ≥ SUSPECT_SCORE_THRESHOLD. */
  suspect: number;
  conversions: number;
}

export interface TimePoint {
  t: string;
  visits: number;
  humans: number;
  bots: number;
  suspect: number;
}

export interface SourceSlice {
  sourceType: SourceType;
  visits: number;
}

export interface CountrySlice {
  /** ISO-3166-1 alpha-2 (uppercase); '' when geo was not captured. */
  country: string;
  visits: number;
}

export interface OverviewData {
  kpis: OverviewKpis;
  series: TimePoint[];
  sources: SourceSlice[];
  /** Top countries by visit volume (descending). */
  countries: CountrySlice[];
  /** Distinct IPs seen more than once in the range (IP-level repeat signal). */
  recidivists: number;
  /** CLD overview aggregates (uniques, devices, threats, top-risk IPs…). */
  extras: OverviewExtras;
}

/** Neutral connection class shown to the client (never the provider's wording). */
export type ConnType = 'residential' | 'datacenter' | 'mobile' | 'unknown';

export interface VisitRowDto {
  visitId: string;
  arrivedAt: string;
  ip: string;
  country: string;
  region: string;
  city: string;
  asn: number;
  asnName: string;
  connType: ConnType;
  /** Any anonymiser signal (proxy / VPN / Tor / relay / anonymous). */
  proxyProbable: boolean;
  sourceType: SourceType;
  referrerDomain: string;
  landingPage: string;
  botName: string;
  botCategory: BotCategory;
  botTrusted: boolean;
  deviceType: string;
  os: string;
  browser: string;
  pageviews: number;
  sessionMs: number;
  /** 0-100 fraud score (0 when the visit was not enriched — cost rule §7). */
  fraudScore: number;
  /** '' | normal | watch | suspect | high_risk | fraud. */
  fraudBand: string;
  /** Visits from this exact IP within the selected range (repeat signal). */
  recidivismIp: number;
  // --- Visits & IP page (V3) — additive, '' / 0 / false when not captured ---
  /** Campaign + source (utm), to render the source badge + the Page·UTM column. */
  utmCampaign: string;
  utmSource: string;
  /** Google Ads click id — drives the "Google Ads" source badge. */
  gclid: string;
  /** UI language tag of the visit (e.g. fr-FR). */
  language: string;
  /** The visit's locale region disagrees with the IP country (a fraud hint). */
  langMismatch: boolean;
  /** Headless browser signal (navigator.webdriver). */
  headless: boolean;
  /** Conversion signals for the Conversion column. */
  formSubmitted: boolean;
  phoneClicks: number;
  /** In-page interaction clicks (NOT ad clicks) — the "Clics" column. */
  clickEvents: number;
  /** This exact IP is currently in the Cloudflare blocklist (active entry). */
  blocked: boolean;
}

export interface VisitsPage {
  rows: VisitRowDto[];
  nextCursor: string | null;
}

/** Visits & IP page (V3) summary cards. connType omits 'mobile' unless the IP
 * intelligence provider actually reports a mobile connection (never approximated).
 * connType + reputation are period-bound; blocklist is the current active state. */
export interface IpSummaryData {
  connType: {
    key: 'residential' | 'datacenter' | 'proxy_vpn' | 'tor' | 'mobile';
    visits: number;
  }[];
  reputation: {
    low: number; // fraud score 0-39
    moderate: number; // 40-69
    high: number; // 70-100
    uniqueIps: number;
  };
  blocklist: {
    /** Individual blocked IPs (kind='ip'). */
    total: number;
    /** Active blocked RANGES (kind='ip_range', e.g. an IPv6 /64) — counted separately. */
    ranges: number;
    datacenter: number;
    vpnProxy: number;
    tor: number;
    manual: number;
  };
}

// --- CLD overview extras + shared rows -------------------------------------

export interface DeviceSlice {
  kind: string;
  visits: number;
}

/** A detected-threat feed row (recent suspect/bot visit). Detected ≠ blocked. */
export interface ThreatRow {
  visitId: string;
  arrivedAt: string;
  ip: string;
  country: string;
  page: string;
  /** Dominant neutral signal: bot | proxy | datacenter | fingerprint | headless | velocity | risk. */
  signal: string;
  score: number;
  isBot: boolean;
}

export interface RiskIpRow {
  ip: string;
  country: string;
  /** Neutral connection class. */
  connType: ConnType;
  proxyProbable: boolean;
  visits: number;
  recidivism: number;
  score: number;
}

/** Extra aggregates for the CLD overview (added to the base OverviewData). */
export interface OverviewExtras {
  uniques: number;
  /** Visits behind a proxy/VPN/Tor/relay. */
  proxy: number;
  /** 0-100 global risk = suspect share. */
  riskScore: number;
  devices: DeviceSlice[];
  sourcesQuality: SourceQuality[];
  recentThreats: ThreatRow[];
  topRiskIps: RiskIpRow[];
}

// --- Realtime --------------------------------------------------------------

export interface HourPoint {
  t: string;
  clean: number;
  fraud: number;
}
export interface RealtimeData {
  activeVisitors: number;
  suspectActive: number;
  feed: ThreatRow[];
  hourly: HourPoint[];
}

// --- Audience --------------------------------------------------------------

export interface DimensionRow {
  key: string;
  visits: number;
  suspect: number;
}
export interface AudienceData {
  countries: DimensionRow[];
  devices: DimensionRow[];
  browsers: DimensionRow[];
  os: DimensionRow[];
}

// --- Geographic map --------------------------------------------------------

export interface GeoCountry {
  country: string;
  visits: number;
  suspect: number;
}
export interface GeoCity {
  city: string;
  country: string;
  visits: number;
  suspect: number;
}
export interface GeoData {
  countries: GeoCountry[];
  cities: GeoCity[];
}

// --- Fraud detection -------------------------------------------------------

export interface FraudKpis {
  fraudSessions: number;
  bots: number;
  proxyVpnTor: number;
  /** Google-Ads-dependent (Palier C); null until connected. */
  fraudClicks: number | null;
}
export interface SignalSlice {
  signal: string;
  count: number;
}
export interface FraudMatrixCell {
  sourceType: SourceType;
  signal: string;
  count: number;
}
export interface FraudData {
  kpis: FraudKpis;
  composition: SignalSlice[];
  feed: ThreatRow[];
  matrix: FraudMatrixCell[];
  autoBlockRate: number | null;
}

// --- Action log (audit journal) --------------------------------------------

export interface LogRow {
  id: number;
  occurredAt: string;
  action: string;
  actor: string | null;
  details: Record<string, unknown> | null;
}
export interface LogData {
  rows: LogRow[];
}

// --- Review & learning -----------------------------------------------------

export type VerdictKind = 'false_positive' | 'real_client' | 'confirmed_fraud' | 'internal_test';

export interface VerdictRow {
  id: number;
  visitId: string | null;
  ip: string | null;
  verdict: VerdictKind;
  note: string | null;
  createdAt: string;
}
export interface ReviewData {
  /** Suspect/bot visits awaiting human qualification. */
  queue: ThreatRow[];
  /** Most recent qualifications. */
  verdicts: VerdictRow[];
}

// --- Blocklist (audit mode — no Cloudflare enforcement until Palier D) -------

export type BlockAction = 'challenge' | 'block';
export type BlockScope = 'site' | 'all';

export interface BlocklistEntry {
  id: number;
  ip: string;
  action: BlockAction;
  scope: BlockScope;
  reason: string | null;
  createdAt: string;
  expiresAt: string | null;
}
export interface BlocklistData {
  entries: BlocklistEntry[];
}

// --- Conversion goals + Conversions page (Palier B) ------------------------

export type GoalCategory =
  | 'purchase'
  | 'form'
  | 'phone'
  | 'newsletter'
  | 'download'
  | 'custom'
  | 'cart';
export type GoalTrigger = 'url' | 'form' | 'phone' | 'whatsapp' | 'download' | 'selector' | 'api';
export type GoalMatchMode = 'contains' | 'equals' | 'regex';
export type GoalValueType = 'none' | 'fixed' | 'url_param' | 'api';

export interface Goal {
  id: number;
  name: string;
  category: GoalCategory;
  triggerType: GoalTrigger;
  matcher: string;
  matchMode: GoalMatchMode;
  valueType: GoalValueType;
  /** Fixed amount in minor units (cents) — always "estimated". */
  valueAmount: number;
  valueParam: string;
  currency: string;
  active: boolean;
}

export const goalInputSchema = z.object({
  name: z.string().min(1).max(80),
  category: z
    .enum(['purchase', 'form', 'phone', 'newsletter', 'download', 'custom', 'cart'])
    .default('custom'),
  triggerType: z.enum(['url', 'form', 'phone', 'whatsapp', 'download', 'selector', 'api']),
  matcher: z.string().max(500).default(''),
  matchMode: z.enum(['contains', 'equals', 'regex']).default('contains'),
  valueType: z.enum(['none', 'fixed', 'url_param', 'api']).default('none'),
  valueAmount: z.coerce.number().int().min(0).max(100_000_000).default(0),
  valueParam: z.string().max(60).default(''),
  currency: z.string().length(3).default('EUR'),
  active: z.boolean().default(true),
});
export type GoalInput = z.infer<typeof goalInputSchema>;

/** Valid = conversions from non-fraudulent sessions; raw = all (incl. fraud). */
export type ConvMode = 'valid' | 'raw';

export interface ConvKpis {
  /** Conversion rate (%). */
  rate: number;
  conversions: number;
  /** Phantom conversions = raw − valid (fraudulent sessions). */
  ghosts: number;
  /** Estimated average basket, minor units. */
  avgValue: number;
}
export interface FunnelStep {
  key: string;
  count: number;
}
export interface ConvTypeSlice {
  category: GoalCategory;
  count: number;
}
export interface ConvPoint {
  t: string;
  conversions: number;
}
export interface ConvSourceRow {
  sourceType: SourceType;
  visits: number;
  suspect: number;
  conversions: number;
}
export interface ConvRecentRow {
  occurredAt: string;
  category: GoalCategory;
  goal: string;
  sourceType: SourceType;
  ip: string;
  country: string;
  /** Estimated value, minor units. */
  value: number;
  currency: string;
  fraudScore: number;
}
export interface ConversionsData {
  mode: ConvMode;
  /** False when the project has no active goal → pedagogical empty state. */
  hasGoals: boolean;
  kpis: ConvKpis;
  funnel: FunnelStep[];
  types: ConvTypeSlice[];
  series: ConvPoint[];
  sources: ConvSourceRow[];
  recent: ConvRecentRow[];
}

// --- Google Ads integration (Palier C, read-only) --------------------------

export interface GoogleAdsStatus {
  /** Platform-side OAuth + developer token + encryption key are all present. */
  configured: boolean;
  /** This org has connected a Google Ads account. */
  connected: boolean;
  /** Accessible customer ids (10 digits) discovered at connect time. */
  customerIds: string[];
  connectedAt: string | null;
}

/** A Google Ads account linked to a specific site (project). */
export interface GoogleAdsSiteAccount {
  customerId: string;
  name: string;
  /** Manager needed to read it ('' when directly accessible) — internal use. */
  loginCustomerId: string;
}

export const linkAccountSchema = z.object({
  customerId: z.string().regex(/^\d{10}$/),
});
export type LinkAccountInput = z.infer<typeof linkAccountSchema>;

// --- Google Ads campaigns & sync (C-fix3) ----------------------------------

/** Accessible account in the MCC hierarchy. Managers (mcc) are NOT selectable. */
export interface GoogleAdsAccountNode {
  id: string;
  name: string;
  type: 'mcc' | 'client';
  parent: string | null;
  children: string[];
  currencyCode: string;
  timeZone: string;
  /** Manager to read this account through ('' when direct) — internal use. */
  loginCustomerId: string;
  accessError?: boolean;
}

export type CampaignStatus = 'active' | 'paused' | 'removed' | 'unknown';

/** A campaign in the account currency. `convVerified` = Silospot-verified
 *  (non-fraud) conversions; null means "— / to configure" (no gclid mapping or
 *  no tracker conversions yet). Google conversions are reference only. */
export interface GoogleAdsCampaign {
  id: string;
  name: string;
  status: CampaignStatus;
  channelType: string;
  budgetMicros: number;
  clicks: number;
  costMicros: number;
  convGoogle: number;
  convVerified: number | null;
  lastActivityAt: string | null;
  fresh: boolean;
}

export interface GoogleAdsCampaignsResult {
  customerId: string;
  currencyCode: string;
  timeZone: string;
  campaigns: GoogleAdsCampaign[];
}

/** Persisted monitoring state for a (site, account): watched set + sync state. */
export interface GoogleAdsWatchState {
  customerId: string;
  accountName: string;
  currencyCode: string;
  timeZone: string;
  periodDays: number;
  autoSync: boolean;
  lastSyncAt: string | null;
  /** Campaigns currently monitored (snapshot metrics). */
  watched: GoogleAdsCampaign[];
  /** New campaigns detected in the account, not yet watched. */
  fresh: GoogleAdsCampaign[];
  /** Non-removed campaigns available in the account. */
  availableCount: number;
}

export const watchCampaignsSchema = z.object({
  customerId: z.string().regex(/^\d{10}$/),
  campaignIds: z.array(z.string().regex(/^\d+$/)).max(500),
  periodDays: z.union([z.literal(30), z.literal(90)]).optional(),
  autoSync: z.boolean().optional(),
});
export type WatchCampaignsInput = z.infer<typeof watchCampaignsSchema>;

// --- Onboarding (C-fix2) ---------------------------------------------------

/** The first incomplete site to resume onboarding on (state is derived, not stored). */
export interface OnboardingResume {
  projectId: string;
  domain: string;
  /** For the step-2 snippet (HMAC public/sign keys — public by design). */
  publicKey: string;
  secretKey: string;
  trackerVerified: boolean;
  adsLinked: boolean;
  /** First incomplete step for this site. */
  step: 'tracker' | 'ads';
}

/**
 * Derived onboarding state for the dashboard lock + setup banner. `locked` is
 * per-account (org): true until at least one site is fully configured (tracker
 * verified AND ≥1 Google Ads account linked).
 */
export interface OnboardingState {
  locked: boolean;
  resume: OnboardingResume | null;
}

export interface GoogleAdsCampaignRow {
  id: string;
  name: string;
  status: string;
  channelType: string;
  impressions: number;
  clicks: number;
  /** Estimated spend in EUR (cost_micros / 1e6). */
  costEur: number;
  conversions: number;
}

/** A manageable Google Ads account (direct or via a manager). */
export interface ManagedAccount {
  id: string;
  name: string;
  manager: boolean;
  level: number;
  /** login-customer-id needed to read this account ('' when directly accessible). */
  loginCustomerId: string;
}

// --- Google Ads audit (Palier C2, waste score — all estimates) -------------

export interface WasteReason {
  code: string;
  label: string;
  /** Contribution to the waste score (informational). */
  points: number;
  detail: string;
}
export interface CampaignWasteResult {
  campaignId: string;
  name: string;
  channelType: string;
  costEur: number;
  clicks: number;
  conversions: number;
  /** 0-100, higher = more likely wasted spend. ESTIMATE. */
  wasteScore: number;
  /** Estimated wasted spend in EUR (costEur × wasteScore/100). */
  wastedEur: number;
  subScores: {
    invalidAndFraud: number;
    network: number;
    placements: number;
    hygiene: number;
    efficiency: number;
  };
  reasons: WasteReason[];
  /** True for a monitored campaign that had NO Google activity in the window: it is
   *  listed (so it is never silently hidden) with zeroed metrics and no waste score —
   *  a running campaign gets clicks, an idle one honestly shows none. */
  noRecentActivity?: boolean;
}
export interface GoogleAdsAudit {
  customerId: string;
  generatedAt: string;
  /** True when served from cache (not a fresh API read). */
  cached: boolean;
  totals: {
    costEur: number;
    clicks: number;
    invalidClicks: number;
    conversions: number;
    /** Σ per-campaign wastedEur — ESTIMATE. */
    wastedEur: number;
    avgWasteScore: number;
  };
  campaigns: CampaignWasteResult[];
}

/**
 * The campaign list to DISPLAY for the monitored set: every watched campaign appears.
 * Watched campaigns with in-window activity carry their audit row; the rest are listed
 * with zeroed metrics and `noRecentActivity` — never a fabricated score, never hidden.
 * Audited (active) campaigns keep their order first; idle ones follow.
 */
export function campaignsForDisplay(
  auditCampaigns: CampaignWasteResult[],
  watched: GoogleAdsCampaign[],
): CampaignWasteResult[] {
  const watchedIds = new Set(watched.map((w) => w.id));
  const active = auditCampaigns.filter((c) => watchedIds.has(c.campaignId));
  const audited = new Set(active.map((c) => c.campaignId));
  const idle: CampaignWasteResult[] = watched
    .filter((w) => !audited.has(w.id))
    .map((w) => ({
      campaignId: w.id,
      name: w.name,
      channelType: w.channelType,
      costEur: 0,
      clicks: 0,
      conversions: 0,
      wasteScore: 0,
      wastedEur: 0,
      subScores: { invalidAndFraud: 0, network: 0, placements: 0, hygiene: 0, efficiency: 0 },
      reasons: [],
      noRecentActivity: true,
    }));
  return [...active, ...idle];
}

export interface GoogleAdsPlacement {
  displayName: string;
  placement: string;
  placementType: string;
  targetUrl: string;
  costEur: number;
  clicks: number;
  conversions: number;
}
export interface GoogleAdsDetails {
  customerId: string;
  placements: GoogleAdsPlacement[];
  exclusions: {
    contentLabelCampaigns: string[];
    negativePlacementCampaigns: string[];
    customerNegativeCount: number;
  };
}

/** Why an entity is recommended for exclusion (ad-click evidence, never a claim). */
export type AdsExclReason = 'recidive' | 'score' | 'pool';

/**
 * One recommended Google Ads exclusion, justified by AD-CLICK evidence only.
 * `kind: 'ip'` is a single address (IPv4, or an IPv6 /64 household); `kind: 'range'`
 * is a rotating pool surfaced as a CIDR/wildcard. Every row has ≥ 1 ad click.
 */
export interface AdsExclusionCandidate {
  /** Paste-ready for Google Ads: an IP, `a.b.c.*`, `a.b.0.0/16`, or `a:b:c:d::/64`. */
  target: string;
  kind: 'ip' | 'range';
  reason: AdsExclReason;
  country: string;
  asn: number;
  asnName: string;
  /** Max fraud score seen on an ad click — probabilistic, never a verdict. */
  score: number;
  /** Lifetime ad clicks from this entity/pool (the "Clics pub" column). */
  adsClicks: number;
  /** Distinct IPs behind a pool range; 1 for a single IP. */
  distinctIps: number;
  /** Already covered by the client's last imported list (or a readable Google block). */
  alreadyExcluded: boolean;
}

/**
 * IP-exclusion view for a Google Ads customer. Ads-only and honest about the API's
 * limit: account-level IP exclusions are NOT readable, so "already excluded" is
 * measured against the client's last imported list.
 */
export interface GoogleAdsIpExclusions {
  customerId: string;
  /** The actionable "to exclude" list — ads-only, strict, sorted by ad clicks. */
  candidates: AdsExclusionCandidate[];
  /** Distinct suspicious entities seen OUTSIDE campaigns (info — Cloudflare handles it). */
  outOfCampaignsCount: number;
  /** Campaign-level IP blocks the API can read (account-level is not readable). */
  googleExclusionCount: number;
  /** When the client last imported their Google exclusion list (ISO), or null. */
  importedAt: string | null;
  /** Number of entries parsed from that imported list. */
  importedCount: number;
}

export interface GoogleAdsSearchTerm {
  term: string;
  campaign: string;
  clicks: number;
  costEur: number;
  /** Google Ads conversions — secondary context only (incomplete). */
  gAdsConversions: number;
  /** 'exclude' = junk/off-topic or majority fraud · 'review' = relevant, no conversion. */
  action: 'exclude' | 'review';
  reason: 'junk' | 'fraud' | 'review';
}
export interface GoogleAdsSearchTermsResult {
  customerId: string;
  /** True once Silospot conversion detection (gclid) is linked to this account. */
  siloConversionAvailable: boolean;
  /** The org runs the Silospot tracker on at least one site. */
  orgHasTracker: boolean;
  terms: GoogleAdsSearchTerm[];
}

// --- Google Ads Claims Center (Palier C4) ----------------------------------

export type ClaimStatus = 'open' | 'prepared' | 'sent' | 'credited' | 'refused';
export const CLAIM_STAGES: ClaimStatus[] = ['open', 'prepared', 'sent', 'credited', 'refused'];

/** One gclid-linked suspicious interaction (IP pseudonymised). */
export interface ClaimProof {
  date: string;
  gclid: string;
  ipMasked: string;
  asn: number;
  device: string;
  durationMs: number;
  fraudScore: number;
  reason: string;
}

export interface GoogleAdsClaimDossier {
  campaignId: string;
  name: string;
  channelType: string;
  period: string;
  costEur: number;
  clicks: number;
  /** Google's own invalid clicks — already filtered & NOT billed (context only). */
  googleInvalidClicks: number;
  invalidClickRate: number;
  /** OUR detection of BILLED suspicious clicks (gclid × tracker fraud). */
  suspectBilledClicks: number;
  /** Estimated contestable amount (suspect billed cost − Google credits) — ESTIMATE. */
  contestableEstimateEur: number;
  status: ClaimStatus;
  googleCaseNumber: string;
  notes: string;
  sentAt: string | null;
  /** gclid-linked suspicious interactions (empty until the tracker is linked). */
  proofs: ClaimProof[];
}

export interface GoogleAdsClaims {
  customerId: string;
  /** True once Silospot billed-suspicious-click detection (gclid) is linked. */
  siloTrackerAvailable: boolean;
  orgHasTracker: boolean;
  totals: {
    contestableEstimateEur: number;
    suspectBilledClicks: number;
    googleInvalidClicks: number;
  };
  /** Count of dossiers per pipeline stage. */
  pipeline: Record<ClaimStatus, number>;
  dossiers: GoogleAdsClaimDossier[];
}

export const claimUpdateSchema = z.object({
  customerId: z.string().regex(/^\d{10}$/),
  campaignId: z.string().min(1).max(32),
  period: z.string().max(16).default(''),
  status: z.enum(['open', 'prepared', 'sent', 'credited', 'refused']).optional(),
  googleCaseNumber: z.string().max(64).optional(),
  notes: z.string().max(2000).optional(),
  estimatedCents: z.coerce.number().int().min(0).max(1_000_000_000).optional(),
});
export type ClaimUpdateInput = z.infer<typeof claimUpdateSchema>;

// --- Visit detail (the row drawer, 5 sections) -----------------------------

export interface VisitSubScores {
  behavior: number;
  network: number;
  fingerprint: number;
  adsToken: number;
  conversion: number;
  security: number;
  proxyPattern: number;
}

export interface JourneyStep {
  kind: 'page' | 'event';
  label: string;
  msSinceLanding: number;
  /** Real time spent on the page (ms); -1 when it can't be derived. */
  dwellMs: number;
}

export interface RecidivismLevels {
  ip: number;
  ip24: number;
  ip16: number;
  asn: number;
  /** Visits sharing this device fingerprint. */
  fingerprint: number;
  /** Distinct IPs the fingerprint was seen on (a strong proxy signal). */
  fingerprintIps: number;
}

/** JA4-translated browser-authenticity verdict (never the raw hash). */
export type AutomationVerdict = 'automated' | 'real' | 'unknown';

export interface VisitOrigin {
  country: string;
  region: string;
  city: string;
  asn: number;
  asnName: string;
  connType: ConnType;
  proxyProbable: boolean;
  browser: string;
  os: string;
  language: string;
  timezone: string;
  automation: AutomationVerdict;
}

export interface VisitDetail {
  visitId: string;
  ip: string;
  fraudScore: number;
  fraudBand: string;
  isBot: boolean;
  botName: string;
  botCategory: BotCategory;
  subScores: VisitSubScores;
  /** Client-safe reasons in plain language (client_score_reasons). */
  clientReasons: string[];
  journey: JourneyStep[];
  recidivism: RecidivismLevels;
  origin: VisitOrigin;
}

export interface ReferrerRow {
  referrerDomain: string;
  visits: number;
}

export interface UtmRow {
  source: string;
  medium: string;
  campaign: string;
  visits: number;
  suspect: number;
}

/** A channel slice with its suspect share (for the per-channel quality view). */
export interface SourceQuality {
  sourceType: SourceType;
  visits: number;
  suspect: number;
}

export interface SourcesData {
  breakdown: SourceQuality[];
  referrers: ReferrerRow[];
  utm: UtmRow[];
}

export interface BotRow {
  botName: string;
  botCategory: BotCategory;
  botTrusted: boolean;
  visits: number;
}

export interface BotCategorySlice {
  botCategory: BotCategory;
  visits: number;
}

export interface BotsData {
  total: number;
  trusted: number;
  untrusted: number;
  /** Human (non-bot) visits in the range — for the humans-vs-bots split. */
  humans: number;
  /** Distinct IPs currently flagged for sensitive-path scanning. */
  scans: number;
  categories: BotCategorySlice[];
  bots: BotRow[];
}

export interface TopIpRow {
  ip: string;
  country: string;
  city: string;
  visits: number;
  lastSeen: string;
  flagged: boolean;
}

export interface FlaggedScan {
  ip: string;
  /** Distinct sensitive paths probed (snapshot at flag time, durable 24h). */
  count: number;
  /** The distinct sensitive paths probed. */
  paths: string[];
  lastSeen: string;
}

export interface TopIpsData {
  ips: TopIpRow[];
  flaggedScans: FlaggedScan[];
}

// --- Recidivism page -------------------------------------------------------

/** A device fingerprint seen on more than one IP (strong proxy signal). */
export interface RecidiveDevice {
  /** Opaque short code derived from the fingerprint (non-reversible, §1). */
  deviceCode: string;
  ipCount: number;
  visits: number;
  /** Operator (ASN) names the device appeared on. */
  networks: string[];
  maxScore: number;
}

/** A /24 network range with the most repeat / suspect activity. */
export interface RecidiveNetwork {
  ip24: string;
  asnName: string;
  country: string;
  suspectVisits: number;
  maxScore: number;
}

export interface RecidiveData {
  /** Distinct IPs seen more than once. */
  recidivistIps: number;
  /** Distinct /24 ranges with repeat activity. */
  net24: number;
  /** Distinct device fingerprints seen on more than one IP. */
  devicesMultiIp: number;
  /** /24 ranges with many distinct active IPs (rotating-pool heuristic). */
  rotatingPools: number;
  devices: RecidiveDevice[];
  networks: RecidiveNetwork[];
}

/** Current-month visit usage for the caller's organisation (Palier 1F). */
export interface UsageSummary {
  plan: string;
  month: string;
  used: number;
  limit: number;
  overLimit: boolean;
  /** ISO timestamp when the grace window ends, or null if not over limit. */
  graceEndsAt: string | null;
  /** True once the grace window has elapsed — new visits are no longer recorded. */
  blocked: boolean;
}

// --- Range fraud detection (ad-click pool detection at the /16 level) -------
/**
 * A suspicious IP RANGE (/16) that clicked the site's ads from a POOL of many
 * distinct IPs. A fraudster rotates IPs within its ISP pool (so the exact IP is
 * useless) but stays inside the same /16 — that is the recoverable signature.
 */
export interface RangeThreat {
  /** The /16 network key, e.g. `178.51.0.0`. */
  cidr16: string;
  /** ISP / operator name. */
  operator: string;
  asn: number;
  /** Ad clicks from this range over the window. */
  adsClicks: number;
  /** Distinct IPs of the range that clicked — the "pool" signal. */
  distinctIps: number;
  /** Distinct /24 sub-ranges touched (spread across the /16 vs concentrated). */
  distinct24: number;
  /** REAL form submissions in the range — the only protection signal. */
  forms: number;
  /** Phone-link clicks — weak/fakeable, never a protection signal. */
  phoneClicks: number;
  /** Median ad-session duration, seconds. */
  medianSec: number;
  /** Average fraud score of the range's ad visits. */
  avgScore: number;
  /** Last ad click from the range (ClickHouse timestamp string). */
  lastSeen: string;
  /** Google Ads exclusion suggestion: CIDR `a.b.0.0/16` or wildcard `a.b.c.*`. */
  suggestion: string;
  /** True when the range has 0 forms → safe to recommend. False → ⚠️ review only. */
  recommend: boolean;
}

/** A single recidivist IP clicking ads abnormally often (not inside a flagged pool). */
export interface IsolatedThreatIp {
  ip: string;
  adsClicks: number;
  forms: number;
  suggestion: string;
  recommend: boolean;
}

/** One day of the LOT-1 rule warm-up tracker (Brussels calendar day). */
export interface RuleAdoptionDay {
  day: string; // YYYY-MM-DD, Europe/Brussels
  adsIpRecidive: number;
  adsRangeRecidive: number;
  tokenSpread: number;
  /** Visits that arrived over IPv6 that day. */
  ipv6: number;
  total: number;
}
export interface RuleAdoptionData {
  days: RuleAdoptionDay[];
}

export interface RangeFraudData {
  windowDays: number;
  totalAdsClicks: number;
  /** Adaptive per-range click threshold (scales with the site's ad volume). */
  threshold: number;
  ranges: RangeThreat[];
  isolatedIps: IsolatedThreatIp[];
  summary: {
    rangesFlagged: number;
    adsClicksFlagged: number;
    /** Flagged ranges carrying ≥1 form (shown with ⚠️, kept out of the safe copy list). */
    withForms: number;
  };
}

/**
 * How a line of the consolidated replacement list came to be:
 * - detected: a /16 pool Silospot detected (may be new to the user's list)
 * - rolled: several of the user's IPs in a form-free /16 → one /16 CIDR
 * - kept_cidr / kept_wildcard / kept_ipv6 / kept_other: the user's own entry preserved
 * - kept_single: a lone user IP kept as-is (0 form, no pool)
 * - protected: a user IP kept individual because its /16 has a real form (never rolled)
 */
export type ConsolidatedKind =
  | 'detected'
  | 'rolled'
  | 'kept_cidr'
  | 'kept_single'
  | 'protected'
  | 'kept_wildcard'
  | 'kept_ipv6'
  | 'kept_other';

export interface ConsolidatedEntry {
  value: string;
  kind: ConsolidatedKind;
  operator: string;
}

export interface ConsolidationResult {
  /** Entries in the pasted (current) list. */
  before: number;
  /** Entries in the consolidated replacement list. */
  after: number;
  /** Every IP the old list covered is still covered (always true by construction). */
  coverageComplete: boolean;
  /** Detected /16 pools that were NOT in the pasted list. */
  newDetected: number;
  /** User IPs kept individual because their /16 has a real form (doctrine). */
  protectedCount: number;
  /** The full replacement list, tagged (for display). */
  entries: ConsolidatedEntry[];
  /** Deduplicated values, capped at the Google Ads limit (~500) — the paste target. */
  copyList: string[];
}
